import { FormattedDate, FormattedMessage } from 'react-intl';
import { PaloColumn, PaloFilterChips, PaloListView, PaloStatusPill } from '@synergy/components-palo';
import { PoolDocumentTitle } from '@synergy/components-next';
import { ReactElement, useEffect } from 'react';
import { getDocumentStatusTranslationId } from '@synergy/utils';

import { Invoice } from '../../../invoices/models/invoice';
import { PaloContactName } from '../../../../common/components/palo/palo-contact-card';
import { PaloDocumentNumberCell } from '../../../../common/components/palo/palo-document-number-cell';
import { PaloInvoiceNumberSequenceFilter } from '../../../../common/components/filter/palo-invoice-number-sequence-filter';
import { fetchAllInvoiceNumberSequences } from '../../../configuration/finance/invoice-number-sequence/store/invoice-number-sequence-actions';
import { invoiceNumberSequencesAllSelector } from '../../../configuration/finance/invoice-number-sequence/store/invoice-number-sequence-selector';
import { requestingSelector } from '../../store/dashboard-selectors';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useDashboardSort } from '../../palo/use-dashboard-sort';
import { useStatusNames } from '../../../../common/components/filter/document-status-filter';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

const columns: PaloColumn<Invoice>[] = [
    {
        key: 'number',
        type: 'documentNumber',
        sortKey: 'number',
        head: <FormattedMessage id="app.table.column.number" />,
        cell: invoice => <PaloDocumentNumberCell number={invoice.number} alert={invoice.receiptAlert} />,
    },
    {
        key: 'status',
        type: 'status',
        sortKey: 'status',
        head: <FormattedMessage id="app.table.column.status" />,
        cell: invoice => (
            <PaloStatusPill
                status={invoice.status}
                label={<FormattedMessage id={getDocumentStatusTranslationId(invoice.status)} />}
            />
        ),
    },
    {
        key: 'type',
        type: 'classification',
        head: <FormattedMessage id="app.table.column.type" />,
        cell: invoice => (
            <FormattedMessage
                id={
                    invoice.isCredit
                        ? 'app.invoices.detail.overview.isCredit.true'
                        : 'app.invoices.detail.overview.isCredit.false'
                }
            />
        ),
    },
    {
        key: 'recipient',
        type: 'name',
        sortKey: 'recipientAccount',
        head: <FormattedMessage id="app.table.column.recipient" />,
        cell: invoice => <PaloContactName company={invoice.recipientAccount} kind="customer" />,
    },
    {
        key: 'supplier',
        type: 'name',
        sortKey: 'supplierAccount',
        head: <FormattedMessage id="app.table.column.supplier" />,
        cell: invoice => <PaloContactName company={invoice.supplierAccount} kind="supplier" />,
    },
    {
        key: 'responsibleContact',
        type: 'name',
        sortKey: 'responsibleContact',
        head: <FormattedMessage id="app.common.responsibleContact" />,
        cell: invoice => <PaloContactName person={invoice.responsibleContact} />,
    },
    {
        // The date the tab opens in order of, as in the list (LST-S16, #2234).
        key: 'invoiceDate',
        type: 'date',
        sortKey: 'invoiceDate',
        head: <FormattedMessage id="app.table.column.invoiceDate" />,
        cell: invoice =>
            invoice.invoiceDate && (
                <FormattedDate value={invoice.invoiceDate} year="numeric" month="short" day="2-digit" />
            ),
    },
    {
        key: 'createdAt',
        type: 'date',
        sortKey: 'createdAt',
        head: <FormattedMessage id="app.table.column.createdAt" />,
        cell: invoice => <FormattedDate value={invoice.createdAt} year="numeric" month="short" day="2-digit" />,
    },
];

export const DashboardOverviewInvoices = (): ReactElement => {
    const statusNames = useStatusNames('invoices');
    const { sort, onSortChange } = useDashboardSort('invoices');
    const invoices = useAppSelector(state => state.invoices.flat);
    const requesting = useAppSelector(requestingSelector);
    const invoiceNumberSequences = useAppSelector(invoiceNumberSequencesAllSelector);
    const dispatch = useThunkDispatch();

    useEffect(() => {
        dispatch(fetchAllInvoiceNumberSequences());
    }, [dispatch]);

    return (
        <>
            <PoolDocumentTitle titleTranslationId="app.navigation.dashboard.invoices" />
            <PaloInvoiceNumberSequenceFilter numberSequences={invoiceNumberSequences} />
            <PaloFilterChips names={{ status: statusNames }} />
            <PaloListView<Invoice>
                embedded
                columns={columns}
                sort={sort}
                onSortChange={onSortChange}
                items={invoices.items}
                getRowId={invoice => invoice.id}
                requesting={requesting}
                emptyLabel="Keine Rechnungen."
                rowHref={invoice => '/invoices/' + invoice.id}
            />
        </>
    );
};
